import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, ChevronRight, Sparkles } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const rise = (delay = 0) => ({
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.6, ease: EASE, delay },
});

const ConceptTag = () => (
    <span className="absolute left-3 top-3 z-20 rounded-full border border-[#0F172A]/10 bg-white/90 px-2 py-0.5 font-mono-brand text-[8px] uppercase tracking-[0.2em] text-[#94A3B8]">
        Concept visual
    </span>
);

const float = {
    animate: { y: [0, -5, 0] },
    transition: { duration: 4.5, repeat: Infinity, ease: "easeInOut" },
};

const CheckItem = ({ children }) => (
    <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-[#475569]">
        <CheckCircle2 className="mt-0.5 h-4.5 w-4.5 h-5 w-5 shrink-0 text-[#0066FF]" strokeWidth={2} />
        {children}
    </li>
);

const AiCard = ({ title, children }) => (
    <motion.div
        {...float}
        className="absolute bottom-4 left-4 right-4 z-20 flex items-center gap-3 rounded-xl border border-[#0F172A]/[0.06] bg-white/95 p-3 shadow-[0_12px_30px_-12px_rgba(11,18,32,0.25)] backdrop-blur-sm"
    >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#0066FF] to-[#A855F7]">
            <Sparkles className="h-3.5 w-3.5 text-white" />
        </span>
        <div className="min-w-0">
            <p className="text-xs font-bold text-[#0F172A]">{title}</p>
            <p className="truncate text-[11px] leading-snug text-[#64748B]">{children}</p>
        </div>
        <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-[#94A3B8]" />
    </motion.div>
);

const CalendarVisual = () => {
    const rows = [
        { day: "Mon 8", bars: [{ l: "32%", w: "24%", c: "from-[#00E5FF] to-[#0066FF]" }] },
        { day: "Tue 9", active: true, bars: [{ l: "14%", w: "30%", c: "from-[#0066FF] to-[#A855F7]" }, { l: "58%", w: "18%", c: "from-[#E0F2FE] to-[#E0F2FE]" }] },
        { day: "Wed 10", bars: [{ l: "8%", w: "22%", c: "from-[#BAE6FD] to-[#BAE6FD]" }, { l: "44%", w: "24%", c: "from-[#00E5FF] to-[#0066FF]" }] },
        { day: "Thu 11", bars: [{ l: "26%", w: "20%", c: "from-[#A855F7] to-[#D946EF]" }] },
        { day: "Fri 12", bars: [{ l: "6%", w: "28%", c: "from-[#E0F2FE] to-[#DDD6FE]" }] },
    ];
    return (
        <div className="relative h-64 overflow-hidden rounded-2xl bg-gradient-to-b from-white to-[#F0F7FF] p-4 pt-9" data-testid="visual-plan">
            <ConceptTag />
            <div className="flex items-center justify-between text-[10px] font-semibold text-[#334155]">
                <span>April 2024</span>
                <div className="flex gap-4 text-[#94A3B8]">
                    <span>8:00</span><span>10:00</span><span>12:00</span><span>14:00</span><span>16:00</span>
                </div>
            </div>
            <div className="relative mt-3 space-y-2">
                <div aria-hidden="true" className="absolute bottom-0 left-[38%] top-0 border-l border-dashed border-[#0066FF]/50" />
                {rows.map((r) => (
                    <div key={r.day} className="flex items-center gap-3">
                        <span className={`flex h-6 w-10 items-center justify-center rounded-full text-[10px] font-medium ${r.active ? "bg-[#0066FF] text-white" : "text-[#64748B]"}`}>
                            {r.day}
                        </span>
                        <div className="relative h-5 flex-1">
                            {r.bars.map((b, i) => (
                                <span key={i} className={`absolute top-1/2 h-2.5 -translate-y-1/2 rounded-full bg-gradient-to-r ${b.c}`} style={{ left: b.l, width: b.w }} />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <AiCard title="AI Suggestion">Best match: Sarah K. · Today, 10:00 – 12:00</AiCard>
        </div>
    );
};

const PhoneVisual = () => (
    <div className="relative flex h-64 items-end justify-center overflow-hidden rounded-2xl bg-gradient-to-b from-white to-[#F5F0FF] p-4 pt-9" data-testid="visual-field">
        <ConceptTag />
        <motion.div
            {...float}
            className="absolute -right-1 top-9 z-20 flex w-40 items-center gap-2.5 rounded-xl border border-[#0F172A]/[0.06] bg-white/95 p-2.5 shadow-[0_12px_30px_-12px_rgba(11,18,32,0.3)] backdrop-blur-sm"
        >
            <Sparkles className="h-4 w-4 shrink-0 text-[#A855F7]" />
            <div>
                <p className="text-[11px] font-bold text-[#0F172A]">AI Check</p>
                <p className="text-[10px] leading-snug text-[#64748B]">All required information is complete.</p>
            </div>
            <CheckCircle2 className="ml-auto h-4 w-4 shrink-0 text-[#0066FF]" />
        </motion.div>
        <div className="w-40 rounded-t-[1.75rem] border-[5px] border-b-0 border-[#0B1220] bg-white shadow-xl">
            <div className="flex items-center justify-between px-3 pt-3 text-[9px] font-semibold text-[#0F172A]">
                <span>‹</span><span>Job Details</span><span>⋯</span>
            </div>
            <div className="px-3 pt-2">
                <span className="rounded-full bg-[#0066FF]/10 px-2 py-0.5 text-[8px] font-semibold text-[#0066FF]">In Progress</span>
                <p className="mt-1.5 text-[11px] font-bold text-[#0F172A]">Boiler Service</p>
                <p className="text-[8px] text-[#94A3B8]">12 Greenway Road, Manchester</p>
                <div className="mt-2 flex gap-2 border-b border-[#0F172A]/[0.06] text-[8px] text-[#94A3B8]">
                    <span>Details</span>
                    <span className="border-b-2 border-[#0066FF] pb-1 font-semibold text-[#0066FF]">Checklist</span>
                    <span>Photos</span>
                    <span>Sign-off</span>
                </div>
                <div className="space-y-1.5 py-2.5 text-[8.5px] text-[#475569]">
                    <p className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-[#0066FF]" />Safety checks completed</p>
                    <p className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-[#0066FF]" />Photos added</p>
                    <p className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border border-[#CBD5E1]" />Customer signature</p>
                    <p className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border border-[#CBD5E1]" />Compliance information</p>
                </div>
            </div>
        </div>
    </div>
);

const InvoicesVisual = () => {
    const bars = [30, 42, 38, 55, 72];
    const months = ["Jan", "Feb", "Mar", "Apr", "May"];
    return (
        <div className="relative h-64 overflow-hidden rounded-2xl bg-gradient-to-b from-white to-[#F0F7FF] p-4 pt-9" data-testid="visual-invoice">
            <ConceptTag />
            <div className="rounded-xl border border-[#0F172A]/[0.06] bg-white p-3.5 shadow-sm">
                <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-[#0F172A]">Invoices</p>
                    <span className="rounded-full border border-[#0F172A]/10 px-2 py-0.5 text-[9px] text-[#64748B]">Last 30 days</span>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                    {[
                        { l: "Total Invoiced", v: "£24,850", d: "12%", c: "text-[#0066FF]" },
                        { l: "Pending", v: "£3,420", d: "8%", c: "text-[#7C5CFC]" },
                        { l: "Exceptions", v: "3", d: "22", c: "text-[#D946EF]" },
                    ].map((s) => (
                        <div key={s.l}>
                            <p className="text-[8px] uppercase tracking-wide text-[#94A3B8]">{s.l}</p>
                            <p className="mt-0.5 text-sm font-extrabold text-[#0F172A]">{s.v}</p>
                            <p className={`flex items-center gap-0.5 text-[8px] font-semibold ${s.c}`}>
                                <ArrowUpRight className="h-2.5 w-2.5" />{s.d}
                            </p>
                        </div>
                    ))}
                </div>
                <div className="mt-3 flex h-14 items-end gap-2.5">
                    {bars.map((h, i) => (
                        <div key={i} className="flex flex-1 flex-col items-center gap-1">
                            <span
                                className={`w-full rounded-t ${i === bars.length - 1 ? "bg-gradient-to-t from-[#0066FF] to-[#A855F7]" : "bg-[#DBEAFE]"}`}
                                style={{ height: `${h}%` }}
                            />
                            <span className="text-[7px] text-[#94A3B8]">{months[i]}</span>
                        </div>
                    ))}
                </div>
            </div>
            <AiCard title="AI Insight">3 invoices may be missing chargeable items.</AiCard>
        </div>
    );
};

const STAGES = [
    {
        id: "plan",
        num: "01",
        title: "Plan & Schedule",
        items: [
            "Capture enquiries and convert them into jobs",
            "AI suggests the best engineer and time slot",
            "Spot clashes, delays and gaps before they happen",
        ],
        visual: <CalendarVisual />,
    },
    {
        id: "execute",
        num: "02",
        title: "Execute in the Field",
        items: [
            "Engineers receive jobs, forms and customer details",
            "AI checks for missing information and compliance steps",
            "Photos, signatures and updates sync instantly",
        ],
        visual: <PhoneVisual />,
    },
    {
        id: "invoice",
        num: "03",
        title: "Invoice & Improve",
        items: [
            "Completed work flows straight into invoicing",
            "AI flags missing items and billing exceptions",
            "Live reporting reveals trends, risks and opportunities",
        ],
        visual: <InvoicesVisual />,
    },
];

export default function EndToEnd() {
    return (
        <section
            className="relative bg-white"
            style={{
                backgroundImage:
                    "linear-gradient(to right, rgba(15,23,42,0.035) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.035) 1px, transparent 1px)",
                backgroundSize: "44px 44px",
            }}
            data-testid="endtoend-section"
            aria-labelledby="endtoend-heading"
        >
            <div className="mx-auto max-w-6xl px-6 py-24 sm:py-32">
                <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
                    <motion.div {...rise(0)}>
                        <span
                            className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/10 bg-white px-4 py-1.5 shadow-sm"
                            data-testid="endtoend-eyebrow"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-[#0066FF]" />
                            <span className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#475569] sm:text-xs">
                                End-to-End Intelligence
                            </span>
                        </span>
                        <h2
                            id="endtoend-heading"
                            className="mt-6 font-display text-4xl font-bold leading-[1.12] tracking-tight text-[#0F172A] sm:text-5xl"
                            data-testid="endtoend-heading"
                        >
                            One intelligent workflow.
                            <br />
                            <span className="text-brand-gradient">From enquiry to invoice.</span>
                        </h2>
                    </motion.div>
                    <motion.p {...rise(0.15)} className="max-w-md text-base leading-relaxed text-[#64748B]" data-testid="endtoend-intro">
                        TedOps AI connects enquiries, scheduling, field teams and invoicing in one
                        intelligent workflow. Every stage is powered by real-time data, automation
                        and AI-driven decision support.
                    </motion.p>
                </div>

                <div className="mt-16 grid gap-6 lg:grid-cols-3">
                    {STAGES.map((stage, i) => (
                        <motion.article
                            key={stage.id}
                            {...rise(0.1 + i * 0.1)}
                            className="flex flex-col rounded-3xl border border-[#0F172A]/[0.07] bg-white p-7 shadow-[0_2px_12px_-4px_rgba(11,18,32,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_45px_-20px_rgba(11,18,32,0.18)] sm:p-8"
                            data-testid={`stage-card-${i + 1}`}
                        >
                            <div className="flex items-center gap-3">
                                <span className="rounded-lg bg-[#0066FF]/10 px-2.5 py-1 font-mono-brand text-xs font-medium text-[#0066FF]">
                                    {stage.num}
                                </span>
                                <h3 className="font-display text-xl font-bold text-[#0F172A]">{stage.title}</h3>
                            </div>
                            <ul className="mt-6 space-y-3.5">
                                {stage.items.map((item) => (
                                    <CheckItem key={item}>{item}</CheckItem>
                                ))}
                            </ul>
                            <div className="mt-auto pt-8">{stage.visual}</div>
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
}
